import { useContext, useMemo, useState } from 'react';
import { AuthContext } from '../../providers/AuthContext';
import { ProductContext } from '../../providers/ProductContext';
import { ProductTagContext } from '../../providers/ProductTagContext';
import { API_ENDPOINTS } from '../../config/api';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { showDeleteConfirm, showError, showSuccess } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';

const ProductTagManager = () => {
  const { token } = useContext(AuthContext);
  const { refetchProducts } = useContext(ProductContext);
  const { productTags, tagGroups, loading, refreshProductTags } = useContext(ProductTagContext);
  const [formData, setFormData] = useState({ name: '', group: 'Temáticas', image: '' });
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [mediaTarget, setMediaTarget] = useState(null);

  const groupedTags = useMemo(() => {
    const groups = new Map();
    productTags.filter(tag => tag._id && tag.isActive !== false).forEach(tag => {
      const group = String(tag.group || 'Otras').trim() || 'Otras';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(tag);
    });
    return [...groups.entries()]
      .map(([group, tags]) => ({ group, tags: tags.sort((a, b) => a.name.localeCompare(b.name, 'es')) }))
      .sort((a, b) => a.group.localeCompare(b.group, 'es'));
  }, [productTags]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = formData.name.trim();
    const group = formData.group.trim() || 'Otras';
    if (!name) return showError('Escribí un nombre para la etiqueta');

    try {
      setSaving(true);
      const response = await fetch(API_ENDPOINTS.productTags.create(), {
        method: 'POST',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ name, group, image: formData.image })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo crear la etiqueta');

      setFormData({ name: '', group, image: '' });
      await refreshProductTags();
      showSuccess(`Etiqueta "${data.name || name}" creada`);
    } catch (error) {
      showError(error.message || 'No se pudo crear la etiqueta');
    } finally {
      setSaving(false);
    }
  };

  const handleMediaSelect = async (media) => {
    const image = String(media?.url || media || '').trim();
    if (!image || !mediaTarget) return;

    if (mediaTarget.type === 'create') {
      setFormData(previous => ({ ...previous, image }));
      setMediaTarget(null);
      return;
    }

    const tag = mediaTarget.tag;
    setMediaTarget(null);
    try {
      setUpdatingId(tag._id);
      const response = await fetch(API_ENDPOINTS.productTags.update(tag._id), {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ image })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo actualizar la imagen');

      await refreshProductTags();
      showSuccess(`Imagen de "${tag.name}" actualizada`);
    } catch (error) {
      showError(error.message || 'No se pudo actualizar la imagen');
    } finally {
      setUpdatingId('');
    }
  };

  const handleDelete = async (tag) => {
    const result = await showDeleteConfirm(
      `Eliminar "${tag.name}" también la quitará de los productos que la tengan asignada.`
    );
    if (!result.isConfirmed) return;

    try {
      setDeletingId(tag._id);
      const response = await fetch(API_ENDPOINTS.productTags.delete(tag._id), {
        method: 'DELETE',
        headers: getAdminAuthHeaders(token)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo eliminar la etiqueta');

      await Promise.all([refreshProductTags(), refetchProducts()]);
      showSuccess(`Etiqueta eliminada${data.productsUpdated ? ` de ${data.productsUpdated} producto(s)` : ''}`);
    } catch (error) {
      showError(error.message || 'No se pudo eliminar la etiqueta');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="tagmgr-panel">
      <div className="tagmgr-header">
        <div>
          <h1><i className="fas fa-tags"></i> Etiquetas de productos</h1>
          <p>Creá etiquetas para organizar el catálogo por tema, público o colección.</p>
        </div>
        <span className="tagmgr-total">{productTags.filter(tag => tag._id && tag.isActive !== false).length} etiquetas</span>
      </div>

      <form className="tagmgr-create-card" onSubmit={handleCreate}>
        <div>
          <label htmlFor="product-tag-name">Nueva etiqueta</label>
          <input
            id="product-tag-name"
            value={formData.name}
            onChange={event => setFormData(previous => ({ ...previous, name: event.target.value }))}
            placeholder="Ej.: Películas"
            maxLength="50"
          />
        </div>
        <div>
          <label htmlFor="product-tag-group">Grupo</label>
          <input
            id="product-tag-group"
            list="product-tag-groups"
            value={formData.group}
            onChange={event => setFormData(previous => ({ ...previous, group: event.target.value }))}
            placeholder="Ej.: Temáticas"
            maxLength="50"
          />
          <datalist id="product-tag-groups">
            {tagGroups.map(group => <option key={group.id} value={group.label} />)}
          </datalist>
        </div>
        <div className="tagmgr-image-field">
          <label>Imagen del botón</label>
          {formData.image ? (
            <div className="tagmgr-image-selected">
              <img src={getDisplayImageUrl(formData.image)} alt="Vista previa de la etiqueta" />
              <button type="button" onClick={() => setMediaTarget({ type: 'create' })}>
                <i className="fas fa-pen"></i> Cambiar
              </button>
              <button
                type="button"
                className="tagmgr-image-remove"
                onClick={() => setFormData(previous => ({ ...previous, image: '' }))}
                aria-label="Quitar imagen seleccionada"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ) : (
            <button type="button" className="tagmgr-image-picker" onClick={() => setMediaTarget({ type: 'create' })}>
              <i className="fas fa-images"></i> Elegir de la biblioteca
            </button>
          )}
        </div>
        <button type="submit" disabled={saving}>
          {saving ? <><i className="fas fa-spinner fa-spin"></i> Guardando...</> : <><i className="fas fa-plus"></i> Crear etiqueta</>}
        </button>
      </form>

      {loading && groupedTags.length === 0 ? (
        <div className="tagmgr-empty"><i className="fas fa-spinner fa-spin"></i> Cargando etiquetas...</div>
      ) : groupedTags.length === 0 ? (
        <div className="tagmgr-empty"><i className="fas fa-tags"></i> Aún no hay etiquetas creadas.</div>
      ) : (
        <div className="tagmgr-groups">
          {groupedTags.map(({ group, tags }) => (
            <section key={group} className="tagmgr-group">
              <h2>{group}<span>{tags.length}</span></h2>
              <div className="tagmgr-tag-list">
                {tags.map(tag => (
                  <article key={tag._id} className="tagmgr-tag-item">
                    <div className="tagmgr-tag-info">
                      <span className="tagmgr-tag-thumb">
                        {tag.image
                          ? <img src={getDisplayImageUrl(tag.image)} alt="" />
                          : <i className="fas fa-image"></i>}
                      </span>
                      <span>{tag.name}</span>
                    </div>
                    <div className="tagmgr-tag-actions">
                      <button
                        type="button"
                        className="tagmgr-tag-image-btn"
                        onClick={() => setMediaTarget({ type: 'existing', tag })}
                        disabled={updatingId === tag._id}
                        aria-label={`${tag.image ? 'Cambiar' : 'Elegir'} imagen de ${tag.name}`}
                        title={tag.image ? 'Cambiar imagen' : 'Elegir imagen'}
                      >
                        {updatingId === tag._id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-images"></i>}
                      </button>
                      <button
                        type="button"
                        className="tagmgr-tag-delete-btn"
                        onClick={() => handleDelete(tag)}
                        disabled={deletingId === tag._id}
                        aria-label={`Eliminar etiqueta ${tag.name}`}
                        title="Eliminar etiqueta"
                      >
                        {deletingId === tag._id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {mediaTarget && (
        <div className="tagmgr-media-overlay" onClick={() => setMediaTarget(null)}>
          <div className="tagmgr-media-modal" role="dialog" aria-modal="true" aria-labelledby="tag-media-title" onClick={event => event.stopPropagation()}>
            <div className="tagmgr-media-header">
              <div>
                <h2 id="tag-media-title">Seleccionar imagen</h2>
                <p>
                  Elegí la imagen para {mediaTarget.type === 'create' ? 'la nueva etiqueta' : `“${mediaTarget.tag.name}”`}.
                </p>
              </div>
              <button type="button" onClick={() => setMediaTarget(null)} aria-label="Cerrar biblioteca">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <EnhancedMediaLibrary mode="selection" allowUpload={false} onSelect={handleMediaSelect} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTagManager;
